import React, { useState, useEffect } from 'react';
import { Activity, ChevronDown, ChevronLeft, Upload, RefreshCw, CheckCircle, XCircle, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { glossaryExtensionService, ExtensionVersion } from '@/services/glossaryExtensionService';
import { glossarySyncService, GlossarySyncStatus } from '@/services/glossarySyncService';
import { GlossaryCategoryManagement } from './GlossaryCategoryManagement';

interface GlossaryManagementSectionProps {
  isExpanded: boolean;
  setIsExpanded: React.Dispatch<React.SetStateAction<boolean>>;
}

const GlossaryManagementSection: React.FC<GlossaryManagementSectionProps> = ({
  isExpanded,
  setIsExpanded
}) => {
  const { toast } = useToast();
  const [file, setFile] = useState<File | null>(null);
  const [releaseNotes, setReleaseNotes] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [currentVersion, setCurrentVersion] = useState<ExtensionVersion | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Sync-related state
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState<GlossarySyncStatus | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [syncSuccess, setSyncSuccess] = useState<string | null>(null);

  // Load current extension version data
  useEffect(() => {
    const loadExtensionData = async () => {
      setIsLoading(true);
      try {
        const version = await glossaryExtensionService.getCurrentVersion();
        setCurrentVersion(version);
      } catch (err) {
        console.error('Error loading extension data:', err);
        setError('Failed to load extension data');
      } finally {
        setIsLoading(false);
      }
    };
    
    loadExtensionData();
  }, []);

  // Subscribe to sync status updates
  useEffect(() => {
    const unsubscribe = glossarySyncService.subscribeSyncStatus((status) => {
      setSyncStatus(status);
    });

    return () => unsubscribe();
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      
      // Check if file is a .zip file
      if (!selectedFile.name.endsWith('.zip')) {
        setError('Please select a valid Chrome extension file (.zip)');
        return;
      }
      
      setFile(selectedFile);
      setError(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file to upload');
      return;
    }

    setIsUploading(true);
    setError(null);
    setSuccess(null);

    try {
      // Upload the extension using our service
      const newVersion = await glossaryExtensionService.uploadExtension(
        file,
        releaseNotes,
        currentVersion
      );
      
      // Update local state
      setCurrentVersion(newVersion);
      
      // Success message
      setSuccess('Chrome extension .zip file uploaded successfully');
      toast({
        title: "Upload Successful",
        description: "Chrome extension .zip file has been uploaded successfully",
        variant: "success",
        className: "bg-green-600 text-white border-green-700",
        duration: 2000,
      });

      // Reset form
      setFile(null);
      setReleaseNotes('');
      
      // Reset file input
      const fileInput = document.getElementById('glossary-extension-file') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
    } catch (err) {
      console.error('Error uploading file:', err);
      setError('Failed to upload file. Please try again.');
      toast({
        title: "Upload Failed",
        description: "Failed to upload Chrome extension .zip file",
        variant: "destructive",
        className: "bg-red-600 text-white border-red-700",
        duration: 2000,
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleSync = async () => {
    setIsSyncing(true);
    setSyncError(null);
    setSyncSuccess(null);

    try {
      const result = await glossarySyncService.triggerSync();
      
      if (result.success && result.result) {
        const metrics = result.result;
        setSyncSuccess(
          `Sync completed successfully! Processed ${metrics.totalTerms} terms: ` +
          `${metrics.newTermsCount} new, ${metrics.updatedTermsCount} updated, ` +
          `${metrics.unchangedTermsCount} unchanged.`
        );
        
        toast({
          title: "Sync Successful",
          description: `Synced ${metrics.totalTerms} glossary terms from Google Sheets`,
          variant: "success",
          className: "bg-green-600 text-white border-green-700",
          duration: 3000,
        });
      } else {
        throw new Error(result.error || 'Unknown sync error');
      }
    } catch (err) {
      console.error('Error syncing glossary:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to sync glossary';
      setSyncError(errorMessage);
      
      toast({
        title: "Sync Failed",
        description: errorMessage,
        variant: "destructive",
        className: "bg-red-600 text-white border-red-700",
        duration: 3000,
      });
    } finally {
      setIsSyncing(false);
    }
  };

  const getSyncStatusIcon = () => {
    if (syncStatus?.inProgress || isSyncing) {
      return <Clock className="h-4 w-4 text-blue-500 animate-pulse" />;
    }
    if (syncStatus?.lastSyncSuccess === false) {
      return <XCircle className="h-4 w-4 text-red-500" />;
    }
    if (syncStatus?.lastSyncSuccess === true) {
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    }
    return <RefreshCw className="h-4 w-4 text-gray-400" />;
  };

  return (
    <div className="p-6 bg-white rounded-lg shadow-sm dark:bg-gray-800">
      <div 
        className="flex items-center gap-2 mb-4 cursor-pointer" 
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Activity className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
        <h2 className="text-xl font-semibold dark:text-white">Glossary Management</h2>
        {isExpanded ? (
          <ChevronDown className="h-5 w-5 ml-auto dark:text-gray-400" />
        ) : (
          <ChevronLeft className="h-5 w-5 ml-auto dark:text-gray-400" />
        )}
      </div>

      {isExpanded && (
        <div className="space-y-4">
          <div className="p-4 border border-gray-200 rounded-md dark:border-gray-700">
            <h3 className="text-lg font-medium mb-2 dark:text-white">Upload Chrome Extension</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Upload the latest version of the Glossary Chrome Extension (.zip file)
            </p>

            <div className="space-y-4">
              <div>
                <label htmlFor="glossary-extension-file" className="block text-sm font-medium mb-1 dark:text-gray-300">
                  Extension File (.zip)
                </label>
                <Input
                  id="glossary-extension-file"
                  type="file"
                  accept=".zip"
                  onChange={handleFileChange}
                  className="dark:bg-gray-700 dark:border-gray-600"
                />
                {file && (
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Selected file: {file.name} ({(file.size / 1024).toFixed(2)} KB)
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="release-notes" className="block text-sm font-medium mb-1 dark:text-gray-300">
                  Release Notes
                </label>
                <Textarea
                  id="release-notes"
                  placeholder="Enter release notes for this version..."
                  value={releaseNotes}
                  onChange={(e) => setReleaseNotes(e.target.value)}
                  rows={4}
                  className="dark:bg-gray-700 dark:border-gray-600"
                />
              </div>

              <Button
                onClick={handleUpload}
                disabled={isUploading || !file}
                className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600"
              >
                <Upload className={`h-4 w-4 ${isUploading ? 'animate-pulse' : ''}`} />
                {isUploading ? 'Uploading...' : 'Upload Extension'}
              </Button>

              {error && (
                <Alert variant="destructive" className="mt-4">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {success && (
                <Alert variant="default" className="mt-4 bg-green-50 text-green-800 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800">
                  <AlertDescription>{success}</AlertDescription>
                </Alert>
              )}
            </div>
          </div>

          {isLoading ? (
            <div className="p-4 border border-gray-200 rounded-md dark:border-gray-700 animate-pulse">
              <h3 className="text-lg font-medium mb-2 dark:text-white">Loading...</h3>
            </div>
          ) : currentVersion ? (
            <div className="p-4 border border-gray-200 rounded-md dark:border-gray-700">
              <h3 className="text-lg font-medium mb-2 dark:text-white">Current Version</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Version: <span className="font-medium dark:text-white">{currentVersion.version}</span>
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Last Updated: <span className="font-medium dark:text-white">{currentVersion.uploadDate}</span>
              </p>
              <div className="mt-4">
                <h4 className="text-md font-medium mb-2 dark:text-white">Release Notes:</h4>
                <div className="p-3 bg-gray-100 rounded-md dark:bg-gray-700 dark:text-gray-300">
                  <p>{currentVersion.releaseNotes}</p>
                </div>
              </div>
              <div className="mt-4">
                <a
                  href={currentVersion.downloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
                >
                  Download Extension
                </a>
              </div>
            </div>
          ) : (
            <div className="p-4 border border-gray-200 rounded-md dark:border-gray-700">
              <h3 className="text-lg font-medium mb-2 dark:text-white">No Extension Uploaded Yet</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Upload a Chrome extension .zip file to make it available for download.
              </p>
            </div>
          )}

          {/* Glossary Sync Section */}
          <div className="p-4 border border-gray-200 rounded-md dark:border-gray-700">
            <h3 className="text-lg font-medium mb-2 dark:text-white">Sync Glossary from Google Sheets</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Sync glossary terms from the Google Sheets spreadsheet to update the database.
            </p>

            <div className="space-y-4">
              {/* Sync Status Display */}
              {syncStatus && (
                <div className="p-3 bg-gray-50 rounded-md dark:bg-gray-700">
                  <div className="flex items-center gap-2 mb-2">
                    {getSyncStatusIcon()}
                    <span className="text-sm font-medium dark:text-white">
                      {syncStatus.inProgress || isSyncing ? 'Sync in Progress...' :
                       syncStatus.lastSyncSuccess === false ? 'Last Sync Failed' :
                       syncStatus.lastSyncSuccess === true ? 'Last Sync Successful' : 'Ready to Sync'}
                    </span>
                  </div>
                  
                  {syncStatus.lastSync && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Last sync: {syncStatus.lastSync.toLocaleString()}
                    </p>
                  )}
                  
                  {syncStatus.totalTerms && (
                    <div className="mt-2 text-xs text-gray-600 dark:text-gray-300">
                      <p>Total terms: {syncStatus.totalTerms}</p>
                      <p>New: {syncStatus.newTermsCount || 0} | Updated: {syncStatus.updatedTermsCount || 0} | Unchanged: {syncStatus.unchangedTermsCount || 0}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Google Sheets Source Info */}
              <div className="p-3 bg-blue-50 rounded-md dark:bg-blue-900/20">
                <p className="text-sm text-blue-800 dark:text-blue-300">
                  <strong>Source:</strong> Google Sheets (ID: 1TeGGJnyYBvkw-iRLC6oTz6VHqsS8gnjcgBVJyXpv0nY)
                </p>
                <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                  Format: Column A (Term) | Column B (Definition)
                </p>
              </div>

              {/* Sync Button */}
              <Button
                onClick={handleSync}
                disabled={isSyncing || (syncStatus?.inProgress ?? false)}
                className="flex items-center gap-2 bg-green-600 hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600"
              >
                <RefreshCw className={`h-4 w-4 ${isSyncing || (syncStatus?.inProgress ?? false) ? 'animate-spin' : ''}`} />
                {isSyncing || (syncStatus?.inProgress ?? false) ? 'Syncing...' : 'Sync Glossary'}
              </Button>

              {/* Sync Error Alert */}
              {syncError && (
                <Alert variant="destructive" className="mt-4">
                  <AlertDescription>{syncError}</AlertDescription>
                </Alert>
              )}

              {/* Sync Success Alert */}
              {syncSuccess && (
                <Alert variant="default" className="mt-4 bg-green-50 text-green-800 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800">
                  <AlertDescription>{syncSuccess}</AlertDescription>
                </Alert>
              )}
            </div>
          </div>

          {/* Category Management Section */}
          <GlossaryCategoryManagement />
        </div>
      )}
    </div>
  );
};

export default GlossaryManagementSection;